// Professional Educational Business Design System
export const theme = {
  colors: {
    primary: {
      50: 'var(--crm-accent-soft, #f0f9ff)',
      100: 'var(--crm-accent-soft, #e0f2fe)',
      200: 'var(--crm-accent-soft, #bae6fd)',
      300: 'var(--crm-accent-soft, #7dd3fc)',
      400: 'var(--crm-accent, #38bdf8)',
      500: 'var(--crm-accent, #0ea5e9)',
      600: 'var(--crm-accent, #0284c7)',
      700: 'var(--crm-accent, #0369a1)',
      800: 'var(--crm-accent, #075985)',
      900: 'var(--crm-accent, #0c4a6e)',
    },
    secondary: {
      50: 'var(--crm-accent-soft, #fdf4ff)',
      100: 'var(--crm-accent-soft, #fae8ff)',
      200: 'var(--crm-accent-soft, #f3d4ff)',
      300: 'var(--crm-accent-soft, #e879f9)',
      400: 'var(--crm-accent, #d946ef)',
      500: 'var(--crm-accent, #c026d3)',
      600: 'var(--crm-accent, #a21caf)',
      700: 'var(--crm-accent, #86198f)',
      800: 'var(--crm-accent, #701a75)',
      900: 'var(--crm-accent, #581c87)',
    },
    neutral: {
      50: 'var(--crm-surface, #f8fafc)',
      100: 'var(--crm-soft, #f1f5f9)',
      200: 'var(--crm-border, #e2e8f0)',
      300: 'var(--crm-border, #cbd5e1)',
      400: 'var(--crm-muted, #94a3b8)',
      500: 'var(--crm-muted, #64748b)',
      600: 'var(--crm-muted, #475569)',
      700: 'var(--crm-text, #334155)',
      800: 'var(--crm-text, #1e293b)',
      900: 'var(--crm-text, #0f172a)',
    },
    success: {
      50: 'var(--crm-success-soft, #f0fdf4)',
      100: 'var(--crm-success-soft, #dcfce7)',
      200: 'var(--crm-success-soft, #bbf7d0)',
      300: 'var(--crm-success-soft, #86efac)',
      400: 'var(--crm-success-text, #4ade80)',
      500: 'var(--crm-success-text, #22c55e)',
      600: 'var(--crm-success-text, #16a34a)',
      700: 'var(--crm-success-text, #15803d)',
      800: 'var(--crm-success-text, #166534)',
      900: 'var(--crm-success-text, #14532d)',
    },
    warning: {
      50: 'var(--crm-warning-soft, #fffbeb)',
      100: 'var(--crm-warning-soft, #fef3c7)',
      200: 'var(--crm-warning-soft, #fde68a)',
      300: 'var(--crm-warning-soft, #fcd34d)',
      400: 'var(--crm-warning-text, #fbbf24)',
      500: 'var(--crm-warning-text, #f59e0b)',
      600: 'var(--crm-warning-text, #d97706)',
      700: 'var(--crm-warning-text, #b45309)',
      800: 'var(--crm-warning-text, #92400e)',
      900: 'var(--crm-warning-text, #78350f)',
    },
    error: {
      50: 'var(--crm-danger-soft, #fef2f2)',
      100: 'var(--crm-danger-soft, #fee2e2)',
      200: 'var(--crm-danger-soft, #fecaca)',
      300: 'var(--crm-danger-soft, #fca5a5)',
      400: 'var(--crm-danger-text, #f87171)',
      500: 'var(--crm-danger-text, #ef4444)',
      600: 'var(--crm-danger-text, #dc2626)',
      700: 'var(--crm-danger-text, #b91c1c)',
      800: 'var(--crm-danger-text, #991b1b)',
      900: 'var(--crm-danger-text, #7f1d1d)',
    },
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
    '5xl': '3rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    full: '9999px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  },
  typography: {
    fontFamily: {
      primary: "'Poppins', 'Inter', 'system-ui', 'sans-serif'",
      secondary: "'Roboto', 'system-ui', 'sans-serif'",
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

export type Theme = typeof theme;